/**
 * SentryVuePlugin.js
 * Vue 3 Sentry composable API for Sentry v8
 * 
 * Features:
 * - useSentry() composable for error capture, breadcrumbs, context, and spans
 * - Imports singleton sentryService directly (no Vue provide/inject needed)
 */

import { sentryService } from './SentryService';

/**
 * Vue 3 Composable API for Sentry
 * 
 * Usage in components:
 * ```
 * import { useSentry } from '@/services/SentryVuePlugin';
 * 
 * const sentry = useSentry();
 * sentry.captureError(error);
 * sentry.addBreadcrumb({ message: 'User action' });
 * ```
 */
export function useSentry() {
  const captureError = (error, context) => {
    if (context !== undefined) {
      sentryService.captureException(error, context);
    } else {
      sentryService.captureException(error);
    }
  };
  const captureMessage = (message, level = 'info', context = {}) => {
    sentryService.captureMessage(message, level, context);
  };
  const addBreadcrumb = breadcrumb => {
    sentryService.addBreadcrumb(breadcrumb);
  };
  const setUser = user => {
    sentryService.setUser(user);
  };
  const setContext = (name, context) => {
    sentryService.setContext(name, context);
  };
  const setTag = (key, value) => {
    sentryService.setTag(key, value);
  };

  /**
   * Run a callback inside a Sentry span (v8 API)
   * 
   * Usage:
   * ```
   * const result = await sentry.startSpan(
   *   { name: 'Load Data', op: 'http.client' },
   *   async (span) => {
   *     const data = await fetchData();
   *     return data;
   *   }
   * );
   * ```
   */
  const startSpan = async (options, callback) => {
    return sentryService.startSpan(options, callback);
  };

  /**
   * Wrap an async function with automatic span tracking (v8 API)
   * 
   * Usage:
   * ```
   * const fetchData = sentry.withSpan(
   *   async () => {
   *     return await api.get('/data');
   *   },
   *   { name: 'fetchData', op: 'http.client' }
   * );
   * ```
   */
  const withSpan = (fn, options) => {
    return async (...args) => {
      try {
        return await startSpan(options, async () => {
          return await fn(...args);
        });
      } catch (error) {
        captureError(error);
        throw error;
      }
    };
  };
  return {
    // Error tracking
    captureError,
    captureMessage,
    addBreadcrumb,
    // Context
    setUser,
    setContext,
    setTag,
    // Performance monitoring (v8 API)
    startSpan,
    withSpan
  };
}
export default useSentry;